import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Change {
  id: string;
  event_type?: string;
  category?: string;
  slug: string;
  description?: string;
  before_extracts?: any;
  after_extract?: any;
  verified?: boolean;
  partially_correct?: boolean;
  invalid?: boolean;
  asset_id?: string;
  tags?: string[];
  asset_ids?: string[];
  labels?: string[];
  annotation_coordinates?: any;
  primary_cropped_url?: string;
  secondary_cropped_url?: string;
  primary_original_url?: string;
  secondary_original_url?: string;
  created_at?: string;
  updated_at?: string;
}

interface Diff {
  id: string;
  page_id: string;
  slug: string;
  v_before: string;
  v_after: string;
  asset_id?: string;
  hook?: string;
  description?: string;
  published?: boolean;
  period?: string;
  delta?: number;
  company_logo_url?: string;
  created_at?: string;
  updated_at?: string;
  changes: Change[];
  page?: {
    id: string;
    subslug?: string;
    pricing_page_url?: string;
    company?: {
      id: string;
      slug: string;
      url?: string;
      logo_url?: string;
    };
  };
}

interface Report {
  id: string;
  title: string;
  period: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  task_id?: string;
  summary?: string;
  stats?: {
    totalDiffs: number;
    totalChanges: number;
    companies: number;
  };
}

interface Statistics {
  totalDiffs: number;
  uniqueCompanies: number;
  totalChanges: number;
  eventTypeCounts: Record<string, number>;
}

interface CachedData<T> {
  data: T;
  lastFetched: number;
  periodFilter: string;
}

interface DiffsState {
  // Lists tab data
  listCache: CachedData<{
    diffs: Diff[];
    totalCount: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  }> | null;
  listStatistics: CachedData<Statistics> | null;

  // Reports tab data
  reportsCache: CachedData<Report[]> | null;

  // Navigation list for edit page (ordered diff IDs as shown in list view)
  navigationList: string[] | null;

  // Current filters
  currentFilters: {
    slug?: string;
    quarter?: string;
    event_type?: string;
    verification_status?: string;
    created_after?: string;
    published?: boolean;
  };
}

const initialState: DiffsState = {
  listCache: null,
  listStatistics: null,
  reportsCache: null,
  navigationList: null,
  currentFilters: {},
};

const diffsSlice = createSlice({
  name: 'diffs',
  initialState,
  reducers: {
    // List tab actions
    setDiffsList: (state, action: PayloadAction<{
      diffs: Diff[];
      totalCount: number;
      currentPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
      periodFilter: string;
    }>) => {
      state.listCache = {
        data: {
          diffs: action.payload.diffs,
          totalCount: action.payload.totalCount,
          currentPage: action.payload.currentPage,
          hasNextPage: action.payload.hasNextPage,
          hasPrevPage: action.payload.hasPrevPage,
        },
        lastFetched: Date.now(),
        periodFilter: action.payload.periodFilter,
      };
    },

    setListStatistics: (state, action: PayloadAction<{
      statistics: Statistics;
      periodFilter: string;
    }>) => {
      state.listStatistics = {
        data: action.payload.statistics,
        lastFetched: Date.now(),
        periodFilter: action.payload.periodFilter,
      };
    },

    // Reports tab actions
    setReportsList: (state, action: PayloadAction<{
      reports: Report[];
      periodFilter: string;
    }>) => {
      state.reportsCache = {
        data: action.payload.reports,
        lastFetched: Date.now(),
        periodFilter: action.payload.periodFilter,
      };
    },

    // Filters
    setDiffsFilters: (state, action: PayloadAction<DiffsState['currentFilters']>) => {
      state.currentFilters = action.payload;
    },

    // Navigation list for edit page
    setNavigationList: (state, action: PayloadAction<string[]>) => {
      state.navigationList = action.payload;
    },

    clearNavigationList: (state) => {
      state.navigationList = null;
    },

    // Legacy compatibility - update single diff
    updateDiffInList: (state, action: PayloadAction<Diff>) => {
      if (state.listCache) {
        const index = state.listCache.data.diffs.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.listCache.data.diffs[index] = action.payload;
        }
      }
    },

    // Legacy compatibility - remove diff
    removeDiffFromList: (state, action: PayloadAction<string>) => {
      if (state.listCache) {
        state.listCache.data.diffs = state.listCache.data.diffs.filter(d => d.id !== action.payload);
        state.listCache.data.totalCount = Math.max(0, state.listCache.data.totalCount - 1);
      }
    },

    // Clear list cache only (preserves navigation list for editor)
    clearDiffsCache: (state) => {
      state.listCache = null;
      state.listStatistics = null;
      state.reportsCache = null;
      // Keep navigationList intact for editor navigation
    },

    // Clear all caches including navigation
    clearDiffsList: (state) => {
      state.listCache = null;
      state.listStatistics = null;
      state.reportsCache = null;
      state.navigationList = null;
      state.currentFilters = {};
    },
  },
});

export const {
  setDiffsList,
  setListStatistics,
  setReportsList,
  setDiffsFilters,
  setNavigationList,
  clearNavigationList,
  updateDiffInList,
  removeDiffFromList,
  clearDiffsCache,
  clearDiffsList,
} = diffsSlice.actions;

export default diffsSlice.reducer;
